/**
 * Geo-Detection for Cookie Consent
 * Detects if user is in EU/EEA region requiring GDPR consent
 * Uses IPinfo Lite (free, unlimited) for IP geolocation
 */

// EU 27 + EEA + UK + Switzerland (31 countries total)
export const EU_EEA_COUNTRIES = [
  // EU 27
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'HR', // Croatia
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden

  // EEA (non-EU)
  'IS', // Iceland
  'LI', // Liechtenstein
  'NO', // Norway

  // Other GDPR-equivalent
  'GB', // United Kingdom
  'CH', // Switzerland
];

// Geo-split v3 (Aug 24, Nitish-ruled): the blocking banner shows ONLY where law
// requires opt-in for analytics — EU/EEA/UK/CH (GDPR/ePrivacy) + China (PIPL:
// explicit consent, no legitimate-interest basis) + Canada (Quebec Law 25
// requires opt-in; the federal OPC recommends it; IPinfo is country-level so
// all-Canada is the safe cut) + South Korea (PIPA: consent for identifiable
// analytics). Everyone else (US + rest, ~84% of traffic) sees NOTHING on
// landing — notice lives in the footer Privacy Policy link and the Cookie
// preferences control, the Figma/Cursor/ChatGPT pattern. Legal verification
// with primary-source citations: agent-side work/cookie_consent/LEGAL_VERDICT_aug24.md.
export const STRICT_CONSENT_COUNTRIES = [...EU_EEA_COUNTRIES, 'CN', 'CA', 'KR'];

const GEO_CACHE_KEY = 'cellcog_geo_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// TESTING OVERRIDE: Set to true to force EU behavior on localhost
const TESTING_OVERRIDE_KEY = 'cellcog_test_force_eu';

/**
 * Check if testing override is enabled
 * @returns {boolean} True if should force EU behavior
 */
const isTestingOverrideEnabled = () => {
  try {
    return localStorage.getItem(TESTING_OVERRIDE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Enable testing override (force EU behavior)
 * Call in browser console: window.enableEUTesting()
 */
export const enableEUTesting = () => {
  localStorage.setItem(TESTING_OVERRIDE_KEY, 'true');
};

/**
 * Disable testing override (normal behavior)
 * Call in browser console: window.disableEUTesting()
 */
export const disableEUTesting = () => {
  localStorage.removeItem(TESTING_OVERRIDE_KEY);
};

// Expose to window for easy testing
if (typeof window !== 'undefined') {
  window.enableEUTesting = enableEUTesting;
  window.disableEUTesting = disableEUTesting;
}

/**
 * Get cached geo data if available and not expired
 * @returns {Object|null} Cached geo data or null
 */
const getCachedGeoData = () => {
  try {
    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (!cached) return null;

    const data = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (within 24 hours)
    if (now - data.timestamp < CACHE_DURATION) {
      return data;
    }

    // Cache expired, remove it
    localStorage.removeItem(GEO_CACHE_KEY);
    return null;
  } catch (error) {
    console.error('[GeoDetection] Error reading cache:', error);
    return null;
  }
};

/**
 * Cache geo data in localStorage
 * @param {Object} geoData - Data to cache
 */
const setCachedGeoData = (geoData) => {
  try {
    const cacheData = {
      ...geoData,
      timestamp: Date.now(),
    };
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('[GeoDetection] Error caching geo data:', error);
  }
};

/**
 * Detect user's geographic location using IPinfo Lite
 * @returns {Promise<Object>} Geo data { isEU, country, city, region }
 */
export const detectUserLocation = async () => {
  // Check testing override first
  if (isTestingOverrideEnabled()) {
    return {
      isEU: true,
      country: 'TEST_EU',
      city: 'Testing',
      region: 'Testing',
      testMode: true,
    };
  }

  // Check cache first
  const cached = getCachedGeoData();
  if (cached) {
    return cached;
  }


  try {
    const response = await fetch('https://ipinfo.io/json', {
      timeout: 5000, // 5 second timeout
    });

    if (!response.ok) {
      throw new Error(`IPinfo request failed: ${response.status}`);
    }

    const data = await response.json();
    const countryCode = data.country?.toUpperCase();

    const geoData = {
      // Field name is historical: isEU now means "strict opt-in bucket"
      // (EU/EEA/UK/CH + CN + CA + KR) — see STRICT_CONSENT_COUNTRIES above.
      isEU: STRICT_CONSENT_COUNTRIES.includes(countryCode),
      country: countryCode,
      city: data.city,
      region: data.region,
    };

    // Cache the result
    setCachedGeoData(geoData);

    return geoData;

  } catch (error) {
    console.error('[GeoDetection] Error detecting location:', error);

    // Safe default: Assume EU to show banner (better safe than sorry)
    const fallbackData = {
      isEU: true,
      country: 'UNKNOWN',
      city: null,
      region: null,
      error: error.message,
    };

    console.warn('[GeoDetection] Using safe fallback (assume EU):', fallbackData);
    return fallbackData;
  }
};

/**
 * Clear cached geo data (useful for testing)
 */
export const clearGeoCache = () => {
  localStorage.removeItem(GEO_CACHE_KEY);
};

// Expose clearGeoCache to window for testing
if (typeof window !== 'undefined') {
  window.clearGeoCache = clearGeoCache;
}
